// R6 — per-pane font-size control: −/+ buttons bracketing a slider (SPEC
// "Reader UX"). Presentational and controlled — the ReadingView owns the
// `FontSizePrefs` state, persistence, and the link behavior; this just renders
// one pane's control and reports changes.

import { useEffect, useRef } from "react";
import { Minus, Plus } from "lucide-react";
import { Button } from "@bindery/ui/components/ui/button";
import { Slider } from "@bindery/ui/components/ui/slider";

export const MIN_FONT_SIZE = 14;
export const MAX_FONT_SIZE = 28;
const STEP = 1;

interface FontSizeControlProps {
  label: string;
  value: number;
  onChange: (next: number) => void;
  /**
   * Reflect the whole control left-to-right. The two-pane text-size bar sits
   * the retelling's control and the original's against the centre divide with
   * the link toggle clasping them, and a reflection is what makes that read as
   * one paired control rather than two identical widgets that happen to be
   * near each other. A true reflection, so the slider inverts with the buttons
   * — a mirror in which the track still filled left-to-right would just look
   * like a bug.
   */
  mirrored?: boolean;
}

function clampFont(n: number): number {
  return Math.min(MAX_FONT_SIZE, Math.max(MIN_FONT_SIZE, n));
}

export function FontSizeControl({
  label,
  value,
  onChange,
  mirrored = false,
}: FontSizeControlProps) {
  // shadcn's Slider (src/components/ui/slider.tsx, immutable) doesn't forward
  // the `aria-label` it's given down to Radix's per-thumb `role="slider"`
  // element — Radix only reads an `aria-label` passed directly to
  // `Slider.Thumb`, which the wrapper never exposes a prop for. Set it
  // imperatively on the rendered thumb instead: synchronizing with a
  // third-party widget's DOM is the one case effects are for.
  const sliderWrapRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const thumb = sliderWrapRef.current?.querySelector('[role="slider"]');
    thumb?.setAttribute("aria-label", label);
  }, [label]);

  const step = (delta: number) => (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="size-7 pointer-coarse:min-h-11 pointer-coarse:min-w-11"
      aria-label={`${delta < 0 ? "Decrease" : "Increase"} ${label}`}
      disabled={delta < 0 ? value <= MIN_FONT_SIZE : value >= MAX_FONT_SIZE}
      onClick={() => {
        onChange(clampFont(value + delta));
      }}
    >
      {delta < 0 ? (
        <Minus className="size-3.5" />
      ) : (
        <Plus className="size-3.5" />
      )}
    </Button>
  );

  return (
    // Mirrored by REORDERING the children, not by `flex-row-reverse`: the
    // latter would leave the DOM in minus → slider → plus order while the eye
    // reads plus → slider → minus, and tab order follows the DOM (WCAG 2.4.3),
    // so focus would jump right, centre, left inside one group.
    // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- a row of buttons laid out as a plain flex box; <fieldset> is the element for form controls and brings its own UA rendering (min-inline-size, legend)
    <div className="flex items-center gap-1.5" role="group" aria-label={label}>
      {step(mirrored ? STEP : -STEP)}
      <span ref={sliderWrapRef} className="contents">
        <Slider
          className="w-24"
          min={MIN_FONT_SIZE}
          max={MAX_FONT_SIZE}
          step={STEP}
          value={[value]}
          // Radix's own inversion, not a CSS transform: it flips the track fill,
          // the pointer mapping and the arrow keys together, so the mirrored
          // control stays coherent for a keyboard as well as for the eye.
          inverted={mirrored}
          aria-label={label}
          onValueChange={(next) => {
            const first = next[0];
            if (first !== undefined) onChange(clampFont(first));
          }}
        />
      </span>
      {step(mirrored ? -STEP : STEP)}
    </div>
  );
}
